import { isEqual, last, pick } from 'lodash';
import { addDays } from 'date-fns';

import { formatDate } from '@lt/components/utils/date';
import {
  getCornerDates,
  getRangeNightsString,
} from './components/DatePicker/components/Calendar/utils';
import { formatServerDate, pluralizeNights } from '../../utils';

import { DEFAULT_TRACKER_PARAMS, enToRusKeyboardMap, FIELD_NAMES } from './constants';

import type { NightsPreset, Nights, StartDatePreset } from '@lt/api/dist/types/searchParams';
import type { DateCell } from './components/DatePicker/types';
import type { PreviousSearchesOption, Values } from './context';
import type {
  CalendarAndPresetsParams,
  CalendarAndPresetsStrings,
  DatePickerTrackerParams,
  DatesRangeType,
  NightsTrackerParams,
  StartDateTrackerParams,
  TrackerParamsFromLandParams,
  TrackerParamsFromValues,
} from './types';
import type { SearchLand } from '@lt/api/dist/methods/search/types';
import type { DatePickerState } from './components/DatePicker';

export const lastStep = (steps: string[]): string | undefined => last(steps);
export const isDestinationStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.destination;
export const afterDestinationStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.destination);
export const isDepartureStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.departure;
export const isDatepickerStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.datepicker;
export const afterDepartureStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.departure);
export const isPopularCountriesStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.popularCountries;
export const isMetaregionsStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.metaregions;
export const afterDatepickerStep = (steps: string[]): boolean =>
  steps.includes(FIELD_NAMES.datepicker);
export const isTouristsStep = (steps: string[]): boolean =>
  lastStep(steps) === FIELD_NAMES.tourists;

export const convertDatesToStringWithEllipsis = (
  dateCell?: DateCell,
  isShort?: boolean
): string => {
  if (!dateCell?.date) return '';

  const { date } = dateCell;

  const formatToken = isShort ? 'd MMM' : 'd MMMM';
  return formatDate(date, formatToken);
};

export const convertCalendarDateToPreset = (
  dateCell?: DateCell,
  shift?: number
): StartDatePreset | null => {
  if (!dateCell?.date || !shift) {
    return null;
  }
  const { date } = dateCell;
  const isShortTitle = !!shift;

  const title = convertDatesToStringWithEllipsis(dateCell, isShortTitle);
  const from = addDays(date, -shift);
  const to = addDays(date, shift);

  return { name: 'calendar_date', title, value: { from, to } };
};

export const convertNightsIntervalToString = (nightsInterval: Nights): string => {
  const { from, to } = nightsInterval;
  const nightsString = `${from === to ? '' : `${from}...`}${to} ${pluralizeNights(to)}`;

  return nightsString;
};

export const convertNightsIntervalToPreset = (
  nightsInterval?: Nights | null
): NightsPreset | undefined => {
  if (!nightsInterval) {
    return;
  }

  return {
    name: 'nights_interval',
    title: convertNightsIntervalToString(nightsInterval),
    value: nightsInterval,
  };
};

export const convertCalendarDatesToNightsInterval = ({
  startDateCell,
  endDateCell,
}: {
  startDateCell: DateCell;
  endDateCell: DateCell;
}): Nights | undefined => {
  return getRangeNightsString({
    startDateCell,
    endDateCell,
  });
};

export const convertDatePickerParamsToStrings = ({
  leftValue,
  rightValue,
  startDatePreset,
  nights,
  nightsPreset,
}: CalendarAndPresetsParams): CalendarAndPresetsStrings => {
  const isPreset = !!(startDatePreset || nights || nightsPreset);

  const leftDateString = convertDatesToStringWithEllipsis(leftValue);
  const rightDateString = convertDatesToStringWithEllipsis(rightValue);

  const startDateOrPreset = leftDateString || startDatePreset?.title || '';

  const nightsString = nights ? `На ${convertNightsIntervalToString(nights)}` : '';
  const endDateOrDuration = rightDateString || nightsPreset?.title || nightsString;

  return {
    isPreset,
    left: startDateOrPreset,
    right: endDateOrDuration,
  };
};

export const isEqualPreviousSearches = (
  s1: PreviousSearchesOption,
  s2: PreviousSearchesOption
): boolean => {
  const destinationEqual = s1.destination.value === s2.destination.value;

  const s1Start = getCornerDates({
    date: s1.datepicker.left.date,
    shift: s1.datepicker.shift,
  });
  const s1End = getCornerDates({ date: s1.datepicker.right.date, shift: s1.datepicker.shift });
  const s2Start = getCornerDates({ date: s2.datepicker.left.date, shift: s2.datepicker.shift });
  const s2End = getCornerDates({ date: s2.datepicker.right.date, shift: s2.datepicker.shift });
  const datepickerEqual = isEqual(s1Start, s2Start) && isEqual(s1End, s2End);

  const touristEqual = isEqual(s1.tourists, s2.tourists);
  const departureEqual = s1.departure?.value === s2.departure?.value;

  return destinationEqual && datepickerEqual && touristEqual && departureEqual;
};

export const convertLatinToRus = (str: string): string =>
  str
    .toLowerCase()
    .split('')
    .map((sym) => enToRusKeyboardMap.get(sym) || sym)
    .join('');

export const getSearchParamsChanges = (lhs: Values, rhs: Values): Partial<Values> => {
  const changes = Object.keys(lhs).reduce((res, cur) => {
    if (!isEqual(lhs[cur], rhs[cur])) {
      return { ...res, [cur]: rhs[cur] };
    }

    return res;
  }, {});

  return changes;
};

// Для формирования параметров события сабмита сигареты

const getDatesRangeType = (datepicker: DatePickerState): DatesRangeType => {
  const { nightsPreset, nights } = datepicker;

  if (nightsPreset) {
    return 'preset';
  }

  if (nights) {
    return 'nights';
  }

  return 'calendar';
};

const getNightsTrackerParams = (datepicker: DatePickerState): NightsTrackerParams => {
  const { nights, nightsPreset, left, right } = datepicker;

  const preparedNights = (nights ||
    nightsPreset?.value ||
    convertCalendarDatesToNightsInterval({
      startDateCell: left,
      endDateCell: right,
    })) as Nights;

  return {
    nights_from: preparedNights.from,
    nights_to: preparedNights.to,
    nights_preset: nightsPreset?.name || null,
  };
};

const getStartDateTrackerParams = ({
  datepicker,
  landParams,
}: {
  datepicker: DatePickerState;
  landParams: SearchLand;
}): StartDateTrackerParams => {
  const startDatePresetValue = datepicker.startDatePreset?.value;
  const startDatePresetTrackerParams = (
    startDatePresetValue
      ? {
          start_date_from: formatServerDate(new Date(startDatePresetValue.from)),
          start_date_till: formatServerDate(new Date(startDatePresetValue.to)),
        }
      : undefined
  ) as Omit<StartDateTrackerParams, 'start_date_preset'> | undefined;

  const startDateTrackerParams = pick(landParams, ['start_date_from', 'start_date_till']) as Omit<
    StartDateTrackerParams,
    'start_date_preset'
  >;

  return {
    ...startDateTrackerParams,
    ...startDatePresetTrackerParams,
    start_date_preset: datepicker.startDatePreset?.name || null,
  };
};

const getKidsAgesTrackerParam = (values: Values): DatePickerTrackerParams['kids_ages'] => {
  return values.tourists.kids.join('|');
};

const getTrackerParamsFromValues = (values: Values): TrackerParamsFromValues | undefined => {
  const { departure, destination, datepicker } = values;
  if (!departure || !destination || !datepicker) return;

  const { initialTab, buttonsClicked } = datepicker;

  const isCountryDestination = ['countries', 'allCountries', 'popularCountries'].includes(
    destination.groupName
  );

  return {
    departure_id: departure.id,
    departure_name: departure.value,
    country_id: (isCountryDestination && destination.value) || null,
    country_iso2: destination.iso2 || null,
    to_place_id: (!isCountryDestination && destination.value) || 0,
    dates_range_type: getDatesRangeType(datepicker),
    kids_ages: getKidsAgesTrackerParam(values),
    ...getNightsTrackerParams(datepicker),
    initial_tab: initialTab || 'calendar',
    buttons_clicked: [buttonsClicked || []].join('|'),
  };
};

const getTrackerParamsFromLandParams = (landParams: SearchLand): TrackerParamsFromLandParams => {
  const { adults, kids } = landParams;

  return {
    ...pick(landParams, ['end_date_from', 'end_date_till', 'search_type']),
    adults_count: adults,
    kids_count: kids,
  } as TrackerParamsFromLandParams;
};

export const getTrackerParams = ({
  values,
  landParams,
}: {
  values: Values;
  landParams: SearchLand;
}): DatePickerTrackerParams | undefined => {
  const { datepicker } = values;
  if (!datepicker) return;

  const trackerParamsFromValues = getTrackerParamsFromValues(values);
  if (!trackerParamsFromValues) return;

  const trackerParamsFromLandParams = getTrackerParamsFromLandParams(landParams);

  const trackerParams = {
    ...DEFAULT_TRACKER_PARAMS,
    ...trackerParamsFromValues,
    ...trackerParamsFromLandParams,
    ...getStartDateTrackerParams({ datepicker, landParams }),
    default_nights_preset: datepicker.nightsPreset?.recommended || false,
  };

  return trackerParams;
};
